<script setup lang="ts">
import type { Diagnostic } from "@/types";
import { computed, ref } from "vue";
import { useTranslation } from "i18next-vue";
import { HEADER_HEIGHT, TABS_HEIGHT } from "../../layout/constants";
import ReportState from "@/stores/report/ReportState";
import { useGetDiagnosticImages } from "@/stores/queries";
import { useMergedReportImages } from "@/composable/useMergedReportImages";
import PhotosViewModeToggle from "./PhotosViewModeToggle.vue";
import PhotosGalleryView from "./PhotosGalleryView.vue";
import PhotosDetailsView from "./PhotosDetailsView.vue";
import PhotosSortingPanel from "./PhotosSortingPanel.vue";
import PhotoSizeControl from "./PhotoSizeControl.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import DRFilesUploaderDialog from "@/components/UI/DRFilesUploaderDialog.vue";
import { FileType } from "@/types/file.types";

const { t } = useTranslation();

const props = defineProps<{
  diagnostic: Diagnostic;
  reportId: number;
}>();

const diagnosticId = computed(() => props.diagnostic.id);
const reportId = computed(() => props.reportId);

// For refetching after upload
const { refetch: refetchDiagnosticImages } =
  useGetDiagnosticImages(diagnosticId);

// Use merged images to get status from ReportImage data
const { mergedImages } = useMergedReportImages(diagnosticId, reportId);

// Modal states
const showUploadPhotos = ref(false);

const isGalleryMode = computed(() => ReportState.photosViewMode === "gallery");
const isDetailsMode = computed(() => ReportState.photosViewMode === "details");

// Section selection state (for filtering photos by section)
const selectedSectionId = ref<number | null>(null);

// Ref to gallery view for clearing selection
const galleryViewRef = ref<InstanceType<typeof PhotosGalleryView> | null>(null);

const handleImagesDrop = () => {
  galleryViewRef.value?.clearSelection();
};

// Stats calculation - now using merged images with ReportImage status
const stats = computed(() => {
  if (!mergedImages.value) return { total: 0, unsorted: 0, sorted: 0 };

  let unsorted = 0;
  let sorted = 0;

  mergedImages.value.forEach((img) => {
    if (img.status === "UNPROCESSED") unsorted++;
    // SORTED or PLACED (has findings) both count as sorted
    if (img.status === "SORTED" || img.status === "PLACED") sorted++;
  });

  return {
    total: mergedImages.value.length,
    unsorted,
    sorted,
  };
});

const topOffset = HEADER_HEIGHT + TABS_HEIGHT;
</script>

<template>
  <div
    class="photos-page d-flex flex-column flex-grow-1"
    :style="`margin-top: ${topOffset}px`"
  >
    <!-- Common subheader -->
    <div
      class="photos-subheader px-4 py-2 border-bottom bg-white d-flex align-items-center justify-content-between"
    >
      <PhotosViewModeToggle />

      <div class="flex-grow-1"></div>

      <!-- Right: Stats -->
      <div
        class="photos-stats d-flex align-items-center gap-3 text-muted small"
      >
        <span>
          <strong class="text-body">{{ stats.total }}</strong>
          {{
            $t("report:photos.stats.photo", {
              count: stats.total,
            })
          }}
        </span>
        <span class="text-muted">•</span>
        <span>
          <strong class="text-body">{{ stats.unsorted }}</strong>
          {{ $t("report:photos.stats.unsorted") }}
        </span>
        <span class="text-muted">•</span>
        <span>
          <strong class="text-body">{{ stats.sorted }}</strong>
          {{ $t("report:photos.stats.sorted") }}
        </span>
      </div>
      <div class="subheader-separator mx-4"></div>

      <PhotoSizeControl :mode="ReportState.photosViewMode" />

      <div class="subheader-separator mx-4"></div>
      <DRBtn
        icon="bi-cloud-upload"
        :label="t('report:photos.actions.import')"
        variant="outline"
        size="sm"
        @click="showUploadPhotos = true"
      />
    </div>

    <!-- Content based on mode -->
    <div class="photos-content flex-grow-1 overflow-hidden d-flex">
      <!-- Gallery mode: image grid with sorting panel on right -->
      <template v-if="isGalleryMode">
        <div class="flex-grow-1" style="min-width: 0">
          <PhotosGalleryView
            ref="galleryViewRef"
            :diagnostic="diagnostic"
            :report-id="reportId"
            :selected-section-id="selectedSectionId"
          />
        </div>
        <PhotosSortingPanel
          :diagnostic-id="diagnostic.id"
          :report-id="reportId"
          :selected-section-id="selectedSectionId"
          @select-section="selectedSectionId = $event"
          @images-dropped="handleImagesDrop"
        />
      </template>

      <!-- Details mode: grouped by sections, no side panel -->
      <template v-if="isDetailsMode">
        <PhotosDetailsView
          :diagnostic="diagnostic"
          :report-id="reportId"
          class="flex-grow-1"
        />
      </template>
    </div>
  </div>

  <!-- Upload photos dialog -->
  <DRFilesUploaderDialog
    v-model="showUploadPhotos"
    :title="t('report:photos.import')"
    :file-type="FileType.DIAGNOSTIC_IMAGE"
    :file-params="{ diagnosticId: diagnostic.id }"
    :upload-description="
      ({ maxFiles, maxSizeMb }) =>
        t('report:only.image.max.size', { maxFiles, maxSizeMb })
    "
    multiple
    images-only
    :extensions="['jpg', 'jpeg', 'png', 'heic', 'gif', 'bmp']"
    @uploaded="refetchDiagnosticImages()"
  />
</template>

<style lang="scss" scoped>
.photos-page {
  height: calc(100vh - v-bind("topOffset + 'px'"));
  width: 100%;
}

.photos-subheader {
  flex-shrink: 0;
}

.subheader-separator {
  width: 1px;
  height: 20px;
  background-color: var(--el-border-color);
}

.photos-content {
  min-height: 0;
  width: 100%;
}

.photos-stats {
  font-size: 13px;
}
</style>
